"use client"
import { useState } from "react"
import { CircleCheck, MinusIcon, PlusIcon, Trash2, X } from "lucide-react"
import { useRouter } from "next/navigation"

import { Button } from "@/components/ui/button"
import {
    Drawer,
    DrawerClose,
    DrawerContent,
    DrawerFooter,
    DrawerHeader,
    DrawerTitle,
    DrawerTrigger,
} from "@/components/ui/drawer"
import Image from "next/image"
import toast from "react-hot-toast"
import { useCart } from "@/store/shipping-cart/useCart"
import type { CartItem } from "@/store/shipping-cart/cart-store"
import { SingleProductEnum } from "@/constants/commerce-enum"
import { DataLayerFactory } from "@/components/google-tag-manager/DataLayerFactory"

interface AddToCartButtonProps {
    product: CartItem
}

export function AddToBasketDrawerMobile({ product }: AddToCartButtonProps) {

    const limit = Math.min(product?.max_basket_limit, product?.min_total_qty_and_max_basket_qty);

    const { addToCart, items, updateItemQuantity } = useCart()
    const [isAdding, setIsAdding] = useState(false)
    const router = useRouter()
    const [isDrawerOpen, setIsDrawerOpen] = useState(false)
    const filteredItem = items?.find((item) => item?.unique_id === product?.unique_id)
    const isProductInCart = !!filteredItem && filteredItem.quantity > 0

    const handleAddToCart = async () => {
        if (!isProductInCart) {
            setIsAdding(true)
            addToCart(product)
            setIsAdding(false)
            const addToCartDataLayer = DataLayerFactory.make("add_to_cart", 0, "", product.product_item_id, 1, "")
            addToCartDataLayer.pushToDataLayer()
            toast.success(SingleProductEnum.addToBasketSuccessfully, {
                position: "top-center",
            })
        }
    }

    const handleUpdateQuantity = async (unique_id: string | null, quantity: number) => {
        if (quantity > limit) {
            toast.error(`بیشتراز ${limit} عدد نمیتوانید به سبد خرید خود اضافه کنید`, {
                position: "top-center",
            });
            return;
        }
        updateItemQuantity(unique_id, quantity)
        if (quantity < 1) {
            setIsDrawerOpen(false)
        }
    }

    const continueShop = () => setIsDrawerOpen(false)

    return (
        <Drawer open={isDrawerOpen} onOpenChange={setIsDrawerOpen}>
            {isProductInCart ? (
                <div className={"w-1/2 flex flex-wrap"}>
                    <div className="w-[140px] border items-center px-2 justify-between rounded-xl border-orange-primary/25 h-10 flex flex-wrap">
                        <Button
                            variant={"link"}
                            className="p-1 w-7 h-7 m-0 rounded-lg"
                            onClick={() => handleUpdateQuantity(filteredItem.unique_id, filteredItem.quantity + 1)}
                        >
                            <PlusIcon className={"w-[18px] h-[18px] text-orange-primary"} />
                        </Button>

                        <div className="w-2 flex items-center justify-center h-6 text-lg text-orange-primary">
                            {filteredItem.quantity.toLocaleString("fa")}
                        </div>

                        <Button
                            onClick={() => handleUpdateQuantity(filteredItem.unique_id, filteredItem.quantity - 1)}
                            variant={"link"}
                            className="p-1 w-7 h-7 m-0 rounded-lg"
                        >
                            {filteredItem.quantity < 2 ? (
                                <Trash2 className={"w-[18px] h-[18px] text-orange-primary"} />
                            ) : (
                                <MinusIcon className={"text-orange-primary"} />
                            )}
                        </Button>
                    </div>
                </div>
            ) : (
                <DrawerTrigger asChild>
                    <Button
                        onClick={handleAddToCart}
                        disabled={isAdding}
                        className="bg-orange-primary rounded-xl text-xs font-bold shadow-none border-none h-[38px] w-[161px]"
                    >
                        {SingleProductEnum.addToCart}
                    </Button>
                </DrawerTrigger>
            )}

            <DrawerContent>
                <div className="w-full">
                    <DrawerHeader className={"w-full flex justify-between text-start !p-5 border-b"}>
                        <DrawerTitle className={"text-sm text-success flex items-center gap-1"}>
                            <CircleCheck className={"p-[1px]"} />
                            {SingleProductEnum.addToBasketSuccessfully}
                        </DrawerTitle>

                        <DrawerClose asChild>
                            <X />
                        </DrawerClose>
                    </DrawerHeader>
                    <div className="w-full h-[137px] flex items-center">
                        <Image
                            src={product?.image || "/placeholder.svg"}
                            alt={"product"}
                            width={97}
                            height={97}
                            className={"w-[97px] h-[97px]"}
                        />
                        <div>
                            <div className={"text-xs pl-4 font-bold text-global-text-color leading-6"}>{product?.title}</div>
                            <div className="w-[160px] mt-3 border items-center px-2 justify-between rounded-xl border-orange-primary/25 h-12 flex flex-wrap">
                                <div className={"text-[#ABABAB] text-xs font-medium"}>{SingleProductEnum.count}:</div>
                                {filteredItem && (
                                    <>
                                        <Button
                                            variant={"link"}
                                            className="p-1 w-7 h-7 m-0 rounded-lg"
                                            onClick={() => handleUpdateQuantity(filteredItem.unique_id, filteredItem.quantity + 1)}
                                        >
                                            <PlusIcon className={"w-[18px] h-[18px] text-orange-primary"} />
                                        </Button>

                                        <div className="w-2 flex items-center justify-center h-6 text-lg text-orange-primary">
                                            {filteredItem.quantity.toLocaleString("fa")}
                                        </div>

                                        <Button
                                            onClick={() => handleUpdateQuantity(filteredItem.unique_id, filteredItem.quantity - 1)}
                                            variant={"link"}
                                            className="p-1 w-7 h-7 m-0 rounded-lg"
                                        >
                                            {filteredItem.quantity < 2 ? (
                                                <Trash2 className={"w-[18px] h-[18px] text-orange-primary"} />
                                            ) : (
                                                <MinusIcon className={"text-orange-primary"} />
                                            )}
                                        </Button>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                    <DrawerFooter className={"w-full !flex flex-row justify-between"}>
                        <Button
                            onClick={() => router.push("/shopping-bag")}
                            disabled={isAdding}
                            className="bg-orange-primary rounded-xl text-xs font-bold shadow-none border-none h-[38px] w-[161px]"
                        >
                            {SingleProductEnum.goToCart}
                        </Button>
                        <Button
                            variant={"outline"}
                            onClick={continueShop}
                            className="rounded-xl text-xs font-bold shadow-none h-[38px] w-[161px]"
                        >
                            {SingleProductEnum.continueBuy}
                        </Button>
                    </DrawerFooter>
                </div>
            </DrawerContent>
        </Drawer>
    )
}

